<template>
  <div class="status-container">
    <div v-if="isUnlistedOrCatalogApproval" class="unlisted-card status-left">
      <div class="product-info">
        <label class="label">Ürün Durumu:</label>
        <div class="tooltip">
          <div
            class="badge-status"
            :style="{
              color: selectedStatus.color,
              background: selectedStatus.bgColor,
            }"
          >
            <Icons
              class="icon"
              :name="selectedStatus.icon"
              :color="selectedStatus.color"
            />
            <span>{{ productStatusDisplayValue }}</span>
          </div>
          <TooltipPlugin
            :tooltipText="selectedStatus.tooltip"
            :tooltipWidth="425"
            :tooltipOffset="[185, 10]"
          >
            <template #tooltip-btn>
              <Icons name="InfoCircle" size="xxs" color="#B4B4B4" />
            </template>
          </TooltipPlugin>
        </div>
      </div>
    </div>

    <template v-else>
      <!-- Product Status Section -->
      <div class="status-card status-left">
        <div class="product-info">
          <label class="label">Ürün Durumu:</label>
          <div class="tooltip">
            <div
              class="badge-status"
              :style="{
                color: selectedStatus.color,
                background: selectedStatus.bgColor,
              }"
            >
              <Icons
                class="icon"
                :name="selectedStatus.icon"
                md
                :color="selectedStatus.color"
              />
              <span>{{ productStatusDisplayValue }}</span>
            </div>
            <TooltipPlugin
              :tooltipText="selectedStatus.tooltip"
              :tooltipWidth="425"
              :tooltipOffset="[185, 10]"
            >
              <template #tooltip-btn>
                <Icons name="InfoCircle" size="xxs" color="#B4B4B4" />
              </template>
            </TooltipPlugin>
          </div>
        </div>
        <button
          v-if="productStatus === 'Active' || productStatus === 'Suspended'"
          @click="openChangeApproveStatusModal"
          class="change"
        >
          Değiştir
        </button>
      </div>

      <!-- Divider -->
      <div class="divider"></div>

      <!-- Sales Status Section -->
      <div
        v-if="shouldShowSalesSection"
        class="status-card-right"
        :style="{
          color: selectedSaleStatus.color,
          background: selectedSaleStatus.bgColor,
        }"
      >
        <label class="label">{{ saleTitleDisplayValue }}:</label>
        <div class="status-row" @click="scrollToSalesVariant">
          <div
            class="badge-sales"
            :style="{
              color: selectedSaleStatus.color,
              background: selectedSaleStatus.bgColor,
            }"
          >
            {{ saleStatusDisplayValue }}
          </div>
          <Icons
            v-if="selectedSaleStatus.icon"
            class="icon"
            :name="selectedSaleStatus.icon"
            md
            :color="selectedSaleStatus.color"
          />
        </div>
      </div>

      <div v-else class="status-card-right" :class="statusCardClass">
        <div class="title" v-if="statusCardContent">
          <label class="label">{{ saleTitleDisplayValue }}: </label>
          <span>{{ statusCardContent.title }}</span>
        </div>

        <span class="description" v-if="statusCardContent.description">
          {{ statusCardContent.description }}
        </span>
      </div>
    </template>
  </div>

  <ChangeApproveStatusModal
    ref="changeApproveStatusModalRef"
    :productStatus="productStatus"
    :productIds="productIds"
    @statusUpdated="handleStatusUpdated"
  />
</template>

<script setup>
import { ref, computed } from "vue";
import { Icons, TooltipPlugin } from "@n11/so-ui-components";
import ChangeApproveStatusModal from "./ChangeApproveStatusModal.vue";
import { sendProductDetailEvent } from "@/utils/ga4/helper";
import { GA4_EVENT } from "@/utils/ga4/event";

const props = defineProps({
  productStatus: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["refetchProductDetail", "scrollToSection"]);

const changeApproveStatusModalRef = ref();

const saleStatusDisplayValue = computed(
  () => props.productStatus.saleStatusDisplayValue
);
const saleStatus = computed(() => props.productStatus.saleStatus || "");
const saleTitleDisplayValue = computed(
  () => props.productStatus.saleTitleDisplayValue
);
const productStatusDisplayValue = computed(
  () => props.productStatus.productStatusDisplayValue
);
const productStatus = computed(() => props.productStatus.productStatus);
const rejectInfo = computed(() => props.productStatus.rejectInfo);
const prohibitedDescription = computed(
  () => props.productStatus.prohibitedDescription
);
const isStatusEditable = computed(() => props.productStatus.isStatusEditable);
const prohibitedReason = computed(() => props.productStatus.prohibitedReason);
const inApprovalReason = computed(() => props.productStatus.inApprovalReason);
const productIds = computed(() => props.productStatus.productIds);

const statusConfig = {
  Active: {
    icon: "Checked",
    tooltip:
      "Bu statüdeki ürünler n11’de satışa açıktır ve müşteriler tarafından görüntülenir. Stok adedi <strong>0’dan büyük</strong> olduğu ve <strong>ürünü bekleme statüsüne almadığınız</strong> sürece satışta kalır.",
    color: "#5EBA57",
    bgColor: "#EDF9EC",
  },
  Suspended: {
    icon: "SuspendedIcon",
    tooltip:
      "Bu statüdeki ürünler, stok adedi <strong>0’dan büyük</strong> olduğu sürece <strong>“Değiştir”</strong> butonu ile yeniden satışa taşınabilir. Stok 0 ise, <strong>“Tükenen”</strong> kategorisinde listelenir.",
    color: "#4FA4E5",
    bgColor: "#E9F4FC",
  },
  Prohibited: {
    icon: "ProhibitedIcon",
    tooltip:
      "Bu ürünler n11 tarafından satışa kapatılmıştır. Detay için <strong>“Yasak Nedeni”</strong> alanına bakabilirsiniz. Düzenleme / itiraz hakkınız varsa, ürün koduyla mağaza desteğe talep açabilir veya canlı desteğe iletebilirsiniz.",
    color: "#D13333",
    bgColor: "#FFEFEF",
  },
  CatalogRejected: {
    icon: "ProhibitedIcon",
    tooltip:
      "Bu statüdeki ürünler, n11 Katalog Ekibi tarafından reddedilmiştir. Detay için <strong>“Red Sebebi”</strong> alanına bakabilirsiniz. Red sebebine göre düzenleyip tekrar öneri yapabilirsiniz.",
    color: "#D13333",
    bgColor: "#FFEFEF",
  },
  Unlisted: {
    icon: "OutOfListIcon",
    tooltip:
      "Bu statüdeki ürünler, <strong>ürün listeleme limitinizin üzerindedir</strong>. Yeniden listelemek için <strong>Ürün Yönetimi > Listeleme Paketleri</strong> bölümünden uygun paket alarak limit dahilinde satışa açabilirsiniz.",
    color: "#6B6B6B",
    bgColor: "#F5F5F5",
  },
  InApproval: {
    icon: "SuspendedIcon",
    tooltip:
      'Bu statüdeki ürünler <strong>"Hata Etiketi"</strong> ile birlikte <strong>"Aksiyon Bekleyenler"</strong> bölümünde listelenir. Hata açıklamasını kontrol ederek <strong>"Onayla"</strong> veya <strong>"Sil"</strong> aksiyonu alabilirsiniz.',
    color: "#4FA4E5",
    bgColor: "#E9F4FC",
  },
  InCatalogApproval: {
    icon: "SuspendedIcon",
    tooltip:
      "Bu statüdeki ürünler öneri sürecini başarılı şekilde tamamlamıştır. Katalog ekibinin kontrolü sonrasında satışa açılabilir veya gerekçeli olarak reddedilebilir. ",
    color: "#4FA4E5",
    bgColor: "#E9F4FC",
  },
};
const selectedStatus = computed(() => statusConfig[productStatus.value]);

const saleStatusConfig = {
  Out_Of_Stock: {
    color: "#D13333",
    bgColor: "#FFEFEF",
    icon: "BottomArrows",
  },
  On_Sale: {
    color: "#5EBA57",
    bgColor: "#EDF9EC",
  },
};

const selectedSaleStatus = computed(() => saleStatusConfig[saleStatus.value]);

const getTitle = (value) => {
  const titleMap = {
    CATEGORY_ERROR: "Hatalı Eşleşen Kategori",
    PRICE_ERROR: "Hatalı Fiyat Şüphesi",
    MATCHED_PRODUCT: "Katalogda Eşleşen Ürün",
    MATCHED_SIMILARITY: "Katalogda Eşleşen Benzer Ürün",
    MATCH_PENDING: "Eşleşme Bekleyen Ürün",
    CBT_PRICE_ERROR: "Gümrük Mevzuatı Sınırlaması",
    UNUSUAL_ORDER_ACTIVITY: "Olağandışı Satış Hacmi Tespiti",
  };
  return titleMap[value] || value;
};

// Status Configuration
const statusCardConfigs = computed(() => ({
  InApproval: {
    class: "inapproval",
    title:
      inApprovalReason.value && inApprovalReason.value.length > 0
        ? getTitle(inApprovalReason.value[0].value)
        : null,
    description:
      inApprovalReason.value && inApprovalReason.value.length > 0
        ? inApprovalReason.value[0].label
        : null,
  },
  Prohibited: {
    class: "prohibited",
    title: prohibitedReason.value || "",
    description: prohibitedDescription.value,
  },
  CatalogRejected: {
    class: "catalogrejected",
    title: "",
    description: rejectInfo.value,
  },
}));

const statusCardContent = computed(() => {
  return statusCardConfigs.value[productStatus.value] || null;
});

const statusCardClass = computed(() => {
  return statusCardConfigs.value[productStatus.value]?.class;
});

const isUnlistedOrCatalogApproval = computed(
  () =>
    productStatus.value === "Unlisted" ||
    productStatus.value === "InCatalogApproval"
);
const shouldShowSalesSection = computed(
  () =>
    !["InApproval", "Prohibited", "CatalogRejected"].includes(
      productStatus.value
    )
);

const handleStatusUpdated = () => {
  emit("refetchProductDetail");
};

const scrollToSalesVariant = () => {
  emit("scrollToSection", 3); // Index 3 is Satış & Varyant Bilgileri
};

const openChangeApproveStatusModal = () => {
  sendProductDetailEvent(GA4_EVENT.BUTTON_CLICK, {
    button_name: 'Değiştir',
    button_type: 'Edit',
    button_area: 'Product and Sales',
    item_id: productIds.value,
  });
  changeApproveStatusModalRef.value?.openChangeApproveStatusModal();
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.status-container {
  display: flex;
  gap: 24px;
  width: 100%;
  align-items: flex-start;
}

.unlisted-card {
  width: 100%;
  min-height: 84px;
  padding: 16px;
  border-radius: 8px;
  border: 0.5px solid $colorN30;
}

.divider {
  width: 0.5px;
  align-self: stretch;
  background-color: $colorN30;
  flex-shrink: 0;
}

.status-card {
  width: 469px;
  min-height: 84px;
  padding: 16px;
  border-radius: 8px;
  border: 0.5px solid $colorN30;
}

.status-left {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: $colorN00;

  .product-info {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .tooltip {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }

  .change {
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: $colorM50;
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: none;
    padding: 0;

    &:hover {
      text-decoration: underline;
    }
  }
}

.status-card-right {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  background: $green-11;
  width: 469px;
  min-height: 84px;
  padding: 16px;
  border-radius: 8px;
}

.title {
  span {
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 18px;
    letter-spacing: -0.05px;
  }
}
.inapproval {
  background: #e9f4fc;
  color: #2b86d1;
}

.prohibited {
  background: #ffefef;
  color: #d13333;
}

.catalogrejected {
  background: #ffefef;
  color: #d13333;
}

.label {
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: -0.05px;
}

.description {
  margin-top: 8px;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.05px;
}

.badge-status {
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
  padding: 6px;
  display: flex;
  border-radius: 4px;
  border: 0.5px solid $colorD30;

  .icon {
    width: 14px;
    height: 14px;
    margin-right: 4px;
    vertical-align: middle;
  }
}

.badge-sales {
  margin-top: 8px;
  background: #e9f8ef;
  color: #2d8a3a;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: -0.05px;
}

.status-row {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
}
</style>
